"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { interleaveCategories, type PortfolioCategory } from "@/lib/portfolio-mix";

/**
 * Real work, tiled behind the hero headline under a heavy black wash.
 *
 * The hero was flat black. This puts the shop's own portfolio behind it —
 * shirts, banners, awards, wraps — dark enough to read as texture rather than
 * as a gallery, so it says "we make things" before anyone reads a word.
 *
 * ── Why it loads after the text, on purpose ─────────────────────────────────
 *
 * Fetched client-side from /api/portfolio, which is exactly what we want here.
 * The headline paints immediately on black and is the LCP element; the mosaic
 * arrives afterwards and fades in. Rendering it server-side would put twelve
 * images in front of the one thing a visitor came to read, to decorate the
 * space behind it.
 *
 * That is also why these are OPTIMISED, unlike the WorkShowcase grid further
 * down the page. Those are the subject and deserve full quality. These sit at
 * 45% opacity under a 70% black wash, where the difference between quality 30
 * and quality 90 is invisible and the difference in bytes is not.
 *
 * ── Readability comes first ─────────────────────────────────────────────────
 *
 * White text over photographs is where contrast quietly dies. Three things
 * keep it safe: the tiles never exceed 45% opacity, a near-solid black layer
 * sits between them and the text, and a radial gradient darkens the middle
 * hardest — which is exactly where the headline is. If the portfolio ever
 * fills with pale images this still reads.
 */
export default function HeroMosaic() {
  /** What is on screen, one entry per cell. */
  const [slots, setSlots] = useState<string[]>([]);
  const [shown, setShown] = useState(false);
  /** Everything we could show, including what is not currently on screen. */
  const pool = useRef<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/portfolio")
      .then((r) => r.json())
      .then((cats: PortfolioCategory[]) => {
        if (cancelled) return;
        // Fetch more than fits. The surplus is what the rotation draws from,
        // and each one only loads when it is actually swapped in, so a deep
        // portfolio costs nothing until it is used.
        pool.current = interleaveCategories(cats, POOL);
        setSlots(pool.current.slice(0, TILES));
        // Next paint, so the fade actually runs instead of the browser
        // collapsing it into the same frame that set the images.
        requestAnimationFrame(() => !cancelled && setShown(true));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // One tile at a time, swapped for a piece of work that is not currently up.
  useEffect(() => {
    if (slots.length === 0) return;
    // A timer is not something a CSS media query can switch off.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const id = setInterval(() => {
      setSlots((current) => {
        const spare = pool.current.filter((url) => !current.includes(url));
        if (spare.length === 0) return current;
        const next = [...current];
        next[Math.floor(Math.random() * next.length)] =
          spare[Math.floor(Math.random() * spare.length)];
        return next;
      });
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [slots.length]);

  // A half-empty grid looks broken rather than sparse, and the hero is fine
  // without it — black was the design before this existed.
  if (slots.length < MIN_TILES) return null;

  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
      <div
        className={[
          "absolute inset-0 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6",
          "hero-drift",
          "transition-opacity duration-1000 ease-out motion-reduce:transition-none",
          shown ? "opacity-[0.55]" : "opacity-0",
        ].join(" ")}
        style={{
          // Fade the top and bottom edges so the grid dissolves into the
          // section instead of ending in a hard line against the header.
          maskImage:
            "linear-gradient(to bottom, transparent, black 18%, black 82%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent, black 18%, black 82%, transparent)",
        }}
      >
        {slots.map((url, i) => (
          // Keyed by POSITION, not by url. Keying by url would unmount the old
          // image and mount the new one, which is a hard cut; keeping the cell
          // mounted is what lets Tile cross-fade between them.
          <Tile key={i} src={url} />
        ))}
      </div>

      {/* The wash, in two layers, and the numbers matter more than they look.
          These multiply: the image survives at
          tile-opacity x (1 - base) x (1 - radial). The first pass at
          0.45 / 0.70 / 0.85 came out at TWO PERCENT through the middle — the
          tiles were technically there and invisible, which is the wrong kind
          of subtle. This lands around 15% behind the headline and 26% at the
          edges: plainly photographs, still nowhere near competing with white
          text over them. */}
      <div className="absolute inset-0 bg-black/45" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 75% 65% at 50% 45%, rgba(0,0,0,0.50) 0%, rgba(0,0,0,0.30) 55%, rgba(0,0,0,0.15) 100%)",
        }}
      />
    </div>
  );
}

/** Enough to fill six columns without the last row being a stub. */
const TILES = 24;
/** Below this it reads as a mistake. */
const MIN_TILES = 12;
/** How many we know about, including the ones waiting off screen. */
const POOL = 60;
/**
 * Gap between swaps.
 *
 * Long. One tile changing every seven seconds is something you notice on the
 * second glance, which is the intent — faster starts competing with the
 * headline for attention, which is the one thing the background must not do.
 */
const ROTATE_MS = 7000;

/**
 * One cell, cross-fading when its image changes.
 *
 * Two stacked layers with the visible one toggling. The alternative — letting
 * React swap the src on a single element — repaints the moment the new file
 * decodes, which is a blink rather than a fade, and looks like a glitch.
 */
function Tile({ src }: { src: string }) {
  const [layers, setLayers] = useState<[string, string | null]>([src, null]);
  const [top, setTop] = useState(false);
  // The updater cannot be trusted to run once: React invokes it twice in
  // development to surface exactly this class of bug, and the first version of
  // this scheduled the reveal from INSIDE it — so the fade was toggled twice
  // and landed back where it started. The current layers are tracked here
  // instead, and every side effect happens in the effect body.
  const current = useRef<[string, string | null]>([src, null]);

  useEffect(() => {
    const [a, b] = current.current;
    if (src === a || src === b) return;

    // Load into whichever layer is hidden, then reveal it next frame — a
    // reveal in the same frame is a cut, not a fade.
    const next: [string, string | null] = top ? [src, b] : [a, src];
    current.current = next;
    setLayers(next);

    const id = requestAnimationFrame(() => setTop((t) => !t));
    return () => cancelAnimationFrame(id);
    // `top` re-runs this after each fade, which is harmless: src is by then
    // one of the two layers and the guard above returns immediately.
  }, [src, top]);

  return (
    <div className="relative aspect-square">
      {layers.map((url, i) =>
        url ? (
          <Image
            key={url}
            src={url}
            // Decorative. The work is described in words further down the
            // page; a screen reader announcing twenty-four identical alt
            // strings behind the headline is noise, not access.
            alt=""
            fill
            sizes="(max-width: 640px) 34vw, (max-width: 1024px) 25vw, 17vw"
            quality={30}
            className={[
              "object-cover transition-opacity duration-[1400ms] ease-in-out",
              "motion-reduce:transition-none",
              (i === 1) === top ? "opacity-100" : "opacity-0",
            ].join(" ")}
          />
        ) : null,
      )}
    </div>
  );
}
